import React, { useRef, useCallback, forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import { ReactComponent as UploadIcon } from 'assets/images/upload.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import { ReactComponent as PdfFileIcon } from 'assets/images/pdf-file.svg';
import { ReactComponent as ErrorIcon } from 'assets/images/cross-error.svg';
import { ReactComponent as UploadedIcon } from 'assets/images/group-covered.svg';
import styles from './styles.module.scss';
import { DefaultButton } from 'components/Buttons/DefaultButton';
import { CircularProgress } from '@mui/material';

type InputProps = {
  selectedFiles: ISelectedFile[];
  placeholder?: string;
  onChange?: (files: ISelectedFile[]) => void;
  onRemoveFile?: (id: string) => void;
  onClick?: () => void;
  isUploadingFiles?: boolean;
  uploadStatus: Map<
    string,
    {
      id: string;
      isLoading?: boolean;
      status?: string;
      error?: string;
    }
  >;
};

export interface ISelectedFile {
  id: string;
  file: File;
  preview: string;
}

const UploadMultipleImagesV2 = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      onChange,
      onRemoveFile,
      onClick,
      selectedFiles,
      uploadStatus,
      isUploadingFiles,
    },
    ref: any
  ) => {
    const inputFile = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(
          Array.from(e.target.files || []).map((file) => {
            return {
              id: uuidv4(),
              file,
              preview: URL.createObjectURL(file),
            };
          })
        );
      }
    };

    const onClickHandler = useCallback(() => {
      if (onClick) {
        onClick();
      } else {
        const fileInput = ref ? ref.current : inputFile.current;
        if (fileInput) {
          fileInput.click();
          fileInput.value = '';
        }
      }
    }, [onClick, ref]);

    return (
      <div className={styles.root} onClick={onClickHandler}>
        <div className={styles.uploadArea}>
          {selectedFiles.length ? (
            <div className={styles.wrapSelectedFiles}>
              <div className={styles.selectedFiles}>
                {selectedFiles.map((file) => (
                  <div key={file.id} className={styles.fileItem}>
                    {file.file.type === 'application/pdf' ? (
                      <PdfFileIcon />
                    ) : (
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className={styles.preview}
                      />
                    )}
                    <div className={styles.filename}>{file.file.name}</div>
                    <div className={styles.action}>
                      {uploadStatus?.get(file.id)?.isLoading ? (
                        <CircularProgress size={20} />
                      ) : uploadStatus?.get(file.id)?.status === 'success' ? (
                        <UploadedIcon />
                      ) : uploadStatus?.get(file.id)?.status === 'error' ? (
                        <ErrorIcon />
                      ) : (
                        <CloseIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onRemoveFile && onRemoveFile(file.id);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.addMore}>
                <span>Add More</span> <AddIcon fontSize="small" />
              </div>
            </div>
          ) : (
            <DefaultButton
              icon={<UploadIcon style={{ fill: '#000' }} />}
              iconPosition="right"
              disabled={!!isUploadingFiles}
            >
              Upload Photos
            </DefaultButton>
          )}
          <input
            type="file"
            multiple
            accept="image/png, image/jpg, image/jpeg, application/pdf"
            onChange={handleChange}
            ref={ref || inputFile}
            disabled={!!isUploadingFiles}
          />
        </div>
      </div>
    );
  }
);

UploadMultipleImagesV2.displayName = 'UploadMultipleImagesV2';

export default UploadMultipleImagesV2;
